import { z } from "zod";

export const DEFAULT_OIDC_USER_SOURCE_SCOPES = [
  "openid",
  "profile",
  "email",
] as const;

export const DEFAULT_OIDC_USER_SOURCE_SUBJECT_CLAIM = "sub" as const;

// Mirrors the Coordinator's per-element rules in
// `apps/coordinator/arcade/app/admin/schemas/user_source.py`. Whitespace
// inside a scope token corrupts the wire format (scopes are joined with
// single spaces on the way to the IdP); we do not otherwise restrict
// the charset — the IdP is the authority on its own scope names.
const WHITESPACE_REGEX = /\s/;

export const scopeTokenSchema = z
  .string()
  .min(1, "Scopes must not be empty")
  .max(1024, "Each scope must be 1024 characters or less")
  .refine(
    (scope) => !WHITESPACE_REGEX.test(scope),
    "Scopes must not contain whitespace"
  );

const scopesSchema = z
  .array(scopeTokenSchema)
  .min(1, "At least one scope is required")
  .max(64, "At most 64 scopes are allowed")
  .refine(
    (scopes) => scopes.includes("openid"),
    "Scopes must include 'openid' (required for OIDC ID token issuance)"
  );

// All fields are non-optional in both input and output (no `.default()` calls)
// so that zodResolver's input/output types match — this lets `useForm` typecheck
// without the resolver returning a `Resolver<z.input, ..., z.output>` mismatch.
// Defaults are applied in the form's `defaultValues` instead.
export const userSourceCreateSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(255),
  description: z.string().max(4000),
  issuer: z.string().trim().url("Must be a valid URL").max(2048),
  client_id: z.string().trim().min(1, "Client ID is required").max(2048),
  client_secret: z.string().min(1, "Client secret is required").max(4096),
  subject_claim: z.string().trim().min(1).max(2048),
  scopes: scopesSchema,
  status: z.enum(["active", "inactive"]),
});

export type UserSourceCreateRequest = z.output<typeof userSourceCreateSchema>;

// Edit-mode form schema: client_secret allows empty (= "keep existing"; stripped
// from the wire payload by the sheet). Same non-optional output shape as the
// create schema so the form type is shared.
export const userSourceEditFormSchema = userSourceCreateSchema.extend({
  client_secret: z.string().max(4096),
});

// Wire shape for PATCH /user_sources/:id. All fields are optional; omitting a
// field means "leave unchanged". Empty `client_secret` is not allowed (server
// requires min_length=1) — callers should omit the key to keep the existing
// secret rather than sending an empty string.
export type UserSourceUpdateRequest = Partial<UserSourceCreateRequest>;

export type UserSourceBindingType = "project" | "organization";

type UserSourceStatus = "active" | "inactive";

type UserSourceProtocol = "oidc";

export type UserSource = {
  id: string;
  organization_id: string;
  project_id: string | null;
  binding_type: UserSourceBindingType;
  name: string;
  description: string;
  protocol: UserSourceProtocol;
  issuer: string;
  client_id: string;
  subject_claim: string;
  scopes: string[];
  status: UserSourceStatus;
  created_at: string;
  updated_at: string;
};

export type UserSourceListEnvelope = {
  code: number;
  msg: string;
  data: {
    items: UserSource[];
    count: number;
    total_count: number;
  };
};
